/**
 * PostHog Analytics Utility
 * Provides typed event tracking for BT Vision
 *
 * Autocapture (clicks, inputs, form submits), pageviews and session replay are
 * handled by PostHog itself — see `initAnalytics`. The named events below are
 * the hand-instrumented ones, for moments a DOM click cannot describe.
 */

import posthog from 'posthog-js';

// Define all trackable events for BT Vision
export type AnalyticsEvent =
  // Auth events
  | 'btv_auth_login_attempt'
  | 'btv_auth_login_success'
  | 'btv_auth_login_failed'
  | 'btv_auth_logout'
  | 'btv_auth_signup_attempt'
  | 'btv_auth_signup_success'
  | 'btv_auth_signup_failed'
  | 'btv_auth_magic_link_sent'
  | 'btv_auth_magic_link_used'
  | 'btv_auth_google_login_attempt'
  | 'btv_auth_google_login_used'
  | 'btv_auth_google_login_failed'
  | 'btv_launch_token_attempt'
  | 'btv_launch_token_success'
  | 'btv_launch_token_failed'
  | 'btv_launch_token_error'

  // Dashboard events
  | 'btv_dashboard_view'
  | 'btv_dashboard_refresh'
  | 'btv_dashboard_tournament_select'
  | 'btv_dashboard_period_change'
  | 'btv_dashboard_filter_apply'

  // Stats & insights events
  | 'btv_stats_card_view'
  | 'btv_stats_card_expand'
  | 'btv_stats_card_share_attempt'
  | 'btv_stats_card_share_success'
  | 'btv_stats_card_download'
  | 'btv_insights_view'
  | 'btv_insights_export'

  // Billing events
  | 'btv_billing_plan_view'
  | 'btv_billing_plan_select'
  | 'btv_billing_checkout_start'
  | 'btv_billing_checkout_success'
  | 'btv_billing_checkout_failed'
  | 'btv_billing_subscription_cancel'
  | 'btv_billing_subscription_update'

  // Settings events
  | 'btv_settings_view'
  | 'btv_settings_profile_update'
  | 'btv_settings_privacy_update'

  // Push notification events. Every event carries `app_host`, so subscribe
  // rates split by origin for free — and since the opt-in UI is hidden on the
  // legacy origin, a nonzero legacy count here is a gate regression, not data.
  | 'btv_push_enable_click'
  | 'btv_push_permission_granted'
  | 'btv_push_permission_denied'
  | 'btv_push_subscribed'
  | 'btv_push_unsubscribed'
  | 'btv_push_test_sent'

  // Navigation events
  | 'btv_nav_dashboard_click'
  | 'btv_nav_stats_click'
  | 'btv_nav_insights_click'
  | 'btv_nav_billing_click'
  | 'btv_nav_settings_click'

  // Tournaments (Meus Torneios) events
  | 'btv_tournaments_view'
  | 'btv_tournaments_share_attempt'
  | 'btv_tournaments_share_blocked'
  | 'btv_tournaments_share_download'

  // Ranking share events
  | 'btv_ranking_share_attempt'
  | 'btv_ranking_share_success'
  | 'btv_ranking_share_download'
  | 'btv_ranking_activation_reminder_share'
  | 'btv_ranking_wildcard_priority_share'

  // Ranking round share events
  | 'btv_ranking_round_share_attempt'
  | 'btv_ranking_round_share_photo_selected'
  | 'btv_ranking_round_share_download'
  | 'btv_ranking_round_share_success'
  | 'btv_ranking_round_share_error'

  // Feature usage events
  | 'btv_feature_tournament_import'
  | 'btv_feature_tournament_import_success'
  | 'btv_feature_tournament_import_failed'
  | 'btv_feature_compare_players'
  | 'btv_feature_share_dashboard'
  | 'btv_feature_export_data'

  // Feature ideas events
  | 'btv_ideas_section_view'
  | 'btv_ideas_locked_click'
  | 'btv_ideas_submit_attempt'
  | 'btv_ideas_submit_success'
  | 'btv_ideas_submit_failed'
  | 'btv_ideas_input_focus'

  // AI Chat events
  | 'btv_ai_ask'
  | 'btv_ai_response'
  | 'btv_ai_error'

  // Upcoming opponents events
  | 'btv_upcoming_view'

  // PWA events
  | 'btv_pwa_install_click'
  | 'btv_pwa_install_completed'
  | 'btv_pwa_ios_guide_view'
  | 'btv_pwa_ios_guide_dismiss'
  | 'btv_pwa_banner_dismiss'

  // btvision.app migration (Phase 3). Fired from the old origin only, so their
  // volume is itself the measure of how much traffic is still stranded there.
  | 'btv_migration_banner_dismiss'
  | 'btv_migration_guide_view'
  | 'btv_migration_open_new_origin'
  | 'btv_migration_email_request'

  // Ranking dashboard notices
  | 'btv_dashboard_pending_match_click'
  | 'btv_dashboard_wildcard_needed_click'
  | 'btv_ranking_stats_open'
  | 'btv_ranking_stats_close'
  | 'btv_ranking_evolution_open'
  | 'btv_ranking_evolution_share'

  // Feedback survey events
  | 'btv_feedback_shown'
  | 'btv_feedback_submit'

  // Landing page events
  | 'btv_landing_cta_click'
  | 'btv_landing_contact_click'

  // Error events
  | 'btv_error_api_failure'
  | 'btv_error_auth_failure'
  | 'btv_error_payment_failure'
  | 'btv_error_network_failure';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://us.i.posthog.com';
const DEBUG = import.meta.env.VITE_POSTHOG_DEBUG === 'true';

/** Set once `initAnalytics` actually starts PostHog; every helper below no-ops until then. */
let started = false;

/**
 * Routes that are rendered by a headless browser, not a person: the
 * server-side video/share capture pages and the local design playgrounds.
 * Tracking them would inflate every usage number and fill session replay with
 * recordings of Puppeteer.
 */
function isRobotRoute(): boolean {
  const path = window.location.pathname;
  return path.startsWith('/capture/') || path.startsWith('/playground/');
}

/** Localhost stays out of PostHog unless VITE_POSTHOG_DEBUG=true asks for it. */
function isLocalhost(): boolean {
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local');
}

/**
 * Boot PostHog. Call once, as early as possible — before React renders.
 *
 * Does nothing without VITE_POSTHOG_KEY, which is how staging, deploy previews
 * and local dev stay out of the data by default.
 */
export function initAnalytics(): void {
  if (started || !POSTHOG_KEY) return;
  if (isRobotRoute()) return;
  if (isLocalhost() && !DEBUG) return;

  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      // Clicks, inputs and form submits, with no per-element instrumentation.
      autocapture: true,
      // The app is a SPA: without this only the cold-start URL is ever counted.
      capture_pageview: 'history_change',
      capture_pageleave: true,
      // Anonymous visitors on the landing page cost a person profile each
      // otherwise; signed-in users get one via setAnalyticsUserId.
      person_profiles: 'identified_only',
      disable_session_recording: false,
      session_recording: {
        // Scores, names and rankings are the product — the only things worth
        // hiding are what people type into fields (emails, CPF, invite codes).
        maskAllInputs: true,
      },
      debug: DEBUG,
    });

    // Stamp every event — autocaptured ones included — with the serving origin.
    // Both domains run the same build during the btvision.app migration, so
    // without this there is no way to tell how much of the active base is still
    // on the old one: the number that decides when Phase 4 can sunset it.
    posthog.register({ app_host: window.location.hostname });

    started = true;
  } catch (error) {
    console.error('[Analytics] Failed to initialise PostHog:', error);
  }
}

/**
 * Track an analytics event with optional properties
 */
export function trackEvent(eventName: AnalyticsEvent, properties?: Record<string, unknown>): void {
  try {
    if (!started) return;
    posthog.capture(eventName, properties);
    if (DEBUG) console.log(`[Analytics] Tracked: ${eventName}`, properties || '');
  } catch (error) {
    console.error('[Analytics] Failed to track event:', error);
  }
}

/**
 * Set the user ID for analytics (pass null to clear on logout).
 *
 * `personProperties` land on the PostHog person, not the event — pass `email`
 * and PostHog shows the account by address instead of a UUID everywhere a
 * person is listed. Safe to call repeatedly: with an unchanged id and
 * unchanged properties, posthog-js sends nothing.
 *
 * Profile data only. Never pass CPF or birth date — they are collected for
 * Asaas, and analytics is not a place they need to exist.
 */
export function setAnalyticsUserId(
  userId: string | null,
  personProperties?: Record<string, unknown>
): void {
  try {
    if (!started) return;
    if (userId) {
      posthog.identify(userId, personProperties);
    } else {
      // Ends the identified session and starts a fresh anonymous one, so the
      // next person on a shared device is not merged into the last one.
      posthog.reset();
    }
    if (DEBUG) console.log(`[Analytics] Set user ID: ${userId}`);
  } catch (error) {
    console.error('[Analytics] Failed to set user ID:', error);
  }
}

/**
 * Identify user with additional properties
 */
export function identifyUser(properties: Record<string, unknown>): void {
  try {
    if (!started) return;
    posthog.setPersonProperties(properties);
    if (DEBUG) console.log('[Analytics] Identified user with properties:', properties);
  } catch (error) {
    console.error('[Analytics] Failed to identify user:', error);
  }
}
